import { useEffect, useMemo, useRef } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormInitialValuesRevoke } from './useFormInitialValuesRevoke.ts';
import { ApplicationRevokeFormValues, FormDataRevoke } from '../types.ts';
import { getRevokeFormConfig } from './getRevokeFormConfig.ts';

export const useApplicationRevokeForm = (
  current: number,
  formData: FormDataRevoke | undefined
) => {
  const { initialValuesRevoke } = useFormInitialValuesRevoke(formData);

  const currentStepRef = useRef(current);
  const formDataRef = useRef<FormDataRevoke | undefined>(formData);

  useEffect(() => {
    currentStepRef.current = current;
  }, [current]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const resolver = useMemo<Resolver<ApplicationRevokeFormValues>>(
    () => async (values, context, options) => {
      const { validationSchema } = getRevokeFormConfig(currentStepRef.current, formDataRef.current);

      if (!validationSchema) {
        return { values, errors: {} };
      }

      const stepResolver = yupResolver(validationSchema) as Resolver<ApplicationRevokeFormValues>;
      return stepResolver(values, context, options);
    },
    []
  );

  const form = useForm<ApplicationRevokeFormValues>({
    defaultValues: initialValuesRevoke,
    resolver,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    form.reset(initialValuesRevoke);
  }, [form, initialValuesRevoke]);

  return form;
};

